import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InlineAuthForm } from '@/components/auth/InlineAuthForm';

export default function ClaimPage() {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { isAuthenticated, isLoading: authLoading } = useAuth();

	const [claimInfo, setClaimInfo] = useState<{ projectName: string; orgName: string; valid: boolean } | null>(null);
	const [loading, setLoading] = useState(true);
	const [claiming, setClaiming] = useState(false);
	const [claimError, setClaimError] = useState<string | null>(null);
	const claimAttempted = useRef(false);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		apiClient.getClaimInfo(token).then(res => {
			if (cancelled) return;
			if (res.success && res.data) {
				setClaimInfo({
					projectName: res.data.projectName,
					orgName: res.data.orgName,
					valid: res.data.valid,
				});
			} else {
				setClaimInfo({ projectName: '', orgName: '', valid: false });
			}
			setLoading(false);
		}).catch(() => {
			if (cancelled) return;
			setClaimInfo({ projectName: '', orgName: '', valid: false });
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [token]);

	// Auto-claim when authenticated and claim info is valid
	useEffect(() => {
		if (!token || !isAuthenticated || !claimInfo?.valid || claimAttempted.current) return;
		claimAttempted.current = true;
		let cancelled = false;
		setClaiming(true);
		setClaimError(null);

		apiClient.claimApp(token).then(res => {
			if (cancelled) return;
			if (res.success && res.data) {
				toast.success(`Claimed ${claimInfo.projectName || 'app'}`);
				navigate(`/chat/${res.data.agentId}`);
			} else {
				setClaiming(false);
				setClaimError(res.error?.message || 'Failed to claim app');
				claimAttempted.current = false;
			}
		}).catch(() => {
			if (cancelled) return;
			setClaiming(false);
			setClaimError('Failed to claim app');
			claimAttempted.current = false;
		});

		return () => { cancelled = true; };
	}, [token, isAuthenticated, claimInfo?.valid, claimInfo?.projectName, navigate]);

	const handleRetry = async () => {
		if (!token) return;
		setClaiming(true);
		setClaimError(null);
		try {
			const res = await apiClient.claimApp(token);
			if (res.success && res.data) {
				toast.success(`Claimed ${claimInfo?.projectName || 'app'}`);
				navigate(`/chat/${res.data.agentId}`);
			} else {
				setClaiming(false);
				setClaimError(res.error?.message || 'Failed to claim app');
			}
		} catch {
			setClaiming(false);
			setClaimError('Failed to claim app');
		}
	};

	if (loading || authLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<div className="space-y-4 text-center">
					<Skeleton className="h-12 w-48 mx-auto" />
					<Skeleton className="h-4 w-32 mx-auto" />
				</div>
			</div>
		);
	}

	if (!claimInfo?.valid) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<Card className="w-full max-w-md">
					<CardHeader>
						<CardTitle>Invalid Link</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm text-muted-foreground">
							This claim link is invalid or the app has already been claimed.
						</p>
						<Button variant="outline" onClick={() => navigate('/')}>
							Go Home
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Authenticated: auto-claim in progress or error with retry
	if (isAuthenticated) {
		if (claimError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-bg-3">
					<Card className="w-full max-w-md">
						<CardHeader>
							<CardTitle>Claim {claimInfo.projectName}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<p className="text-sm text-muted-foreground">
								{claimError || 'Something went wrong while claiming. Please try again.'}
							</p>
							<Button onClick={handleRetry} disabled={claiming}>
								{claiming ? 'Claiming...' : 'Try Again'}
							</Button>
						</CardContent>
					</Card>
				</div>
			);
		}

		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<Card className="w-full max-w-md">
					<CardHeader>
						<CardTitle>Claiming {claimInfo.projectName}</CardTitle>
					</CardHeader>
					<CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" />
						<span>Claiming app...</span>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Unauthenticated: show claim info with inline auth form
	return (
		<div className="min-h-screen flex items-center justify-center bg-bg-3">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>Claim {claimInfo.projectName}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-sm text-muted-foreground">
						Sign in or create an account to claim <strong>{claimInfo.projectName}</strong>
						{claimInfo.orgName ? <> and join <strong>{claimInfo.orgName}</strong></> : null}.
					</p>
					<InlineAuthForm
						defaultMode="register"
						intendedUrl={`/claim/${token}`}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
